import type { FullTranslation } from "../../lib/i18n"

export const EXTRACTIONS_T = {
  // ── Library ──
  tabExtractions: { FR: "Extractions", EN: "Extractions" },
  libraryTitle: { FR: "Extractions", EN: "Extractions" },
  libraryDescription: {
    FR: "Des sélections enregistrées de vos leads. Attachées à une campagne, elles décident quels leads partent automatiquement dans votre outil. Gratuit : chaque lead trouvé reste gardé et facturé, les autres attendent un envoi manuel.",
    EN: "Saved selections of your leads. Attached to a campaign, they decide which leads are pushed to your tool automatically. Free: every lead found is still kept and billed, the others wait for a manual sync.",
  },
  freeNote: {
    FR: "Gratuit. Une extraction ne supprime jamais un lead : elle décide seulement lesquels sont envoyés automatiquement. Les autres restent visibles et peuvent être synchronisés à la main.",
    EN: "Free. An extraction never removes a lead: it only decides which ones are pushed automatically. The others stay visible and can be synced by hand.",
  },
  newExtraction: { FR: "Nouvelle extraction", EN: "New extraction" },
  editExtraction: { FR: "Modifier l'extraction", EN: "Edit extraction" },
  edit: { FR: "Modifier", EN: "Edit" },
  delete: { FR: "Supprimer", EN: "Delete" },
  cancel: { FR: "Annuler", EN: "Cancel" },
  save: { FR: "Enregistrer", EN: "Save" },
  empty: {
    FR: "Aucune extraction pour l'instant.",
    EN: "No extraction yet.",
  },
  loadFailed: {
    FR: "Impossible de charger les extractions.",
    EN: "Failed to load the extractions.",
  },
  usedBy: {
    FR: "Utilisée par {n} {n|campagne|campagnes}",
    EN: "Used by {n} {n|campaign|campaigns}",
  },
  unused: {
    FR: "Attachée à aucune campagne",
    EN: "Not attached to any campaign",
  },
  deleteTitle: { FR: "Supprimer {name} ?", EN: "Delete {name}?" },
  deleteBody: {
    FR: "Aucun lead n'est supprimé. Les campagnes qui l'utilisent ({n}) revoient leurs leads pas encore envoyés sans elle.",
    EN: "No lead is deleted. The campaigns using it ({n}) re-check their leads not pushed yet without it.",
  },
  savedToast: { FR: "Extraction enregistrée", EN: "Extraction saved" },
  deletedToast: { FR: "Extraction supprimée", EN: "Extraction deleted" },

  // ── Form ──
  nameLabel: { FR: "Nom", EN: "Name" },
  namePlaceholder: {
    FR: "ex. Avec email et bien notés",
    EN: "e.g. With email and well rated",
  },
  nameRequired: { FR: "Donnez-lui un nom.", EN: "Give it a name." },
  criteriaLegend: { FR: "Critères", EN: "Criteria" },
  criteriaHelp: {
    FR: "Un lead est sélectionné quand il remplit tous les critères choisis.",
    EN: "A lead is selected when it meets every criterion you pick.",
  },
  any: { FR: "Peu importe", EN: "Any" },
  contactLabel: { FR: "Coordonnées", EN: "Contact details" },
  contactWithEmail: { FR: "Avec email", EN: "With email" },
  contactWithPhone: { FR: "Avec téléphone", EN: "With phone" },
  contactWithAny: {
    FR: "Avec email ou téléphone",
    EN: "With email or phone",
  },
  contactWithBoth: {
    FR: "Avec email et téléphone",
    EN: "With email and phone",
  },
  qualityLabel: { FR: "Qualité du site", EN: "Website quality" },
  qualityHigh: { FR: "Bonne (75 et plus)", EN: "High (75 and up)" },
  qualityMedium: { FR: "Moyenne (40 à 74)", EN: "Medium (40 to 74)" },
  qualityLow: { FR: "Faible (moins de 40)", EN: "Low (under 40)" },
  qualityUnrated: { FR: "Non notée", EN: "Not rated" },
  minRatingLabel: { FR: "Note minimale", EN: "Minimum rating" },
  minRatingHelp: { FR: "De 0 à 5.", EN: "From 0 to 5." },
  minReviewsLabel: { FR: "Avis minimum", EN: "Minimum reviews" },
  adsLabel: { FR: "Publicité", EN: "Advertising" },
  adsYes: { FR: "Fait de la publicité", EN: "Advertising" },
  adsNo: { FR: "Pas de publicité", EN: "Not advertising" },
  websiteLabel: { FR: "Site web", EN: "Website" },
  websiteYes: { FR: "A un site", EN: "Has a website" },
  websiteNo: { FR: "Sans site", EN: "No website" },
  aiRuleLabel: { FR: "Règle IA (facultative)", EN: "AI rule (optional)" },
  aiRulePlaceholder: {
    FR: "ex. Garder uniquement les entreprises dont le site tourne sous WordPress",
    EN: "e.g. Only keep companies whose site runs on WordPress",
  },
  aiRuleHelp: {
    FR: "Un agent la vérifie sur chaque lead, gratuitement. Tapez @ pour vous appuyer sur un enrichissement (ex. @ads pour la publicité, @company pour le chiffre d'affaires) : la campagne doit le faire tourner.",
    EN: "An agent checks it on every lead, for free. Type @ to rely on an enrichment (e.g. @ads for advertising, @company for turnover): the campaign must run it.",
  },
  aiRuleInvalidRefs: {
    FR: "Enrichissement inconnu : {refs}. Choisissez-en un dans le menu @ ou retirez-le.",
    EN: "Unknown enrichment: {refs}. Pick one from the @ menu or remove it.",
  },
  criteriaRequired: {
    FR: "Choisissez au moins un critère ou écrivez une règle IA.",
    EN: "Pick at least one criterion or write an AI rule.",
  },
  previewAction: {
    FR: "Tester sur cette campagne",
    EN: "Try on this campaign",
  },
  previewResult: {
    FR: "{kept} sur {total} {total|lead remplit|leads remplissent} les critères (hors règle IA).",
    EN: "{kept} of {total} {total|lead meets|leads meet} the criteria (AI rule aside).",
  },
  aiRuleChip: { FR: "Règle IA", EN: "AI rule" },

  // ── Campaign attachment ──
  pickerTitle: { FR: "Envoi automatique", EN: "Automatic sending" },
  pickerAll: { FR: "Tous les leads", EN: "All leads" },
  pickerAllHelp: {
    FR: "Chaque lead trouvé est envoyé dans votre outil.",
    EN: "Every lead found is sent to your tool.",
  },
  pickerSome: { FR: "Seulement certains leads", EN: "Only some leads" },
  pickerSomeHelp: {
    FR: "Seuls les leads qui correspondent aux extractions cochées sont envoyés. Les autres restent dans votre liste.",
    EN: "Only the leads matching the ticked extractions are sent. The others stay in your list.",
  },
  pickerSomeEmpty: {
    FR: "Cochez au moins une extraction, sinon tous les leads sont envoyés.",
    EN: "Tick at least one extraction, otherwise every lead is sent.",
  },
  pickerNone: {
    FR: "Tous les leads sont envoyés automatiquement.",
    EN: "Every lead is sent automatically.",
  },
  pickerAttachError: {
    FR: "{name} cite {refs}, que cette campagne ne fait pas tourner. Activez l'enrichissement ou décochez l'extraction.",
    EN: "{name} tags {refs}, which this campaign does not run. Enable the enrichment or untick the extraction.",
  },
  onlyExtractedHelp: {
    FR: "Votre liste de leads ne montre que les leads extraits. Décochez pour voir aussi les autres et les envoyer à la main. Le CRM et les pages publiques ne montrent que les leads extraits dans tous les cas.",
    EN: "Your leads list only shows the extracted leads. Untick to see the others too and send them by hand. The CRM and the public pages only show the extracted leads either way.",
  },
  reviewAction: { FR: "Relancer la revue IA", EN: "Re-run the AI review" },
  reviewQueued: {
    FR: "{n} {n|lead renvoyé|leads renvoyés} à la revue IA",
    EN: "{n} {n|lead|leads} queued for the AI review",
  },

  // ── Campaign bar (overview) ──
  barNone: { FR: "Aucune extraction", EN: "No extraction" },
  barNoneHint: {
    FR: "Tous les leads partent dans votre outil.",
    EN: "Every lead goes to your tool.",
  },
  barMore: { FR: "+{n}", EN: "+{n}" },
  retentionHint: {
    FR: "{pct} % des leads passent vos extractions",
    EN: "{pct}% of the leads pass your extractions",
  },
  segAll: { FR: "Tous", EN: "All" },
  segSent: { FR: "Déjà envoyés", EN: "Already sent" },
  segSentHint: {
    FR: "Déjà envoyés : ils ne bougent plus, même si vous changez d'extraction.",
    EN: "Already sent: they stay put, even if you change extractions.",
  },
  segReady: { FR: "À envoyer", EN: "To send" },
  segReadyHint: {
    FR: "Passent vos extractions, partent au prochain envoi.",
    EN: "Pass your extractions, go out with the next send.",
  },
  segExcluded: { FR: "Écartés", EN: "Left out" },
  segExcludedHint: {
    FR: "Gardés et facturés, mais pas envoyés. Envoi à la main possible.",
    EN: "Kept and billed, but not sent. Can be sent by hand.",
  },
  segPendingHint: {
    FR: "{n} en attente de la revue IA",
    EN: "{n} awaiting the AI review",
  },

  // ── Switcher ──
  switcherLabel: {
    FR: "Extractions de la campagne",
    EN: "Campaign extractions",
  },
  hideExcluded: { FR: "Masquer les écartés", EN: "Hide the left out" },
  manage: { FR: "Gérer", EN: "Manage" },
  apply: { FR: "Appliquer", EN: "Apply" },
  impactLocked: {
    FR: "{n} déjà {n|envoyé ne bouge|envoyés ne bougent} pas",
    EN: "{n} already sent {n|stays|stay} put",
  },
  impactEnter: {
    FR: "{n} {n|lead entre|leads entrent}",
    EN: "{n} {n|lead joins|leads join}",
  },
  impactLeave: {
    FR: "{n} {n|lead sort|leads sortent}",
    EN: "{n} {n|lead leaves|leads leave}",
  },
  impactPending: {
    FR: "{n} {n|attend|attendent} la revue IA",
    EN: "{n} {n|awaits|await} the AI review",
  },
  impactFuture: { FR: "Prochains leads", EN: "Next leads" },
  impactFutureHint: {
    FR: "Les prochains leads trouvés suivront ce choix.",
    EN: "The next leads found follow this choice.",
  },
  appliedToast: {
    FR: "Extractions appliquées · {n} {n|envoyé inchangé|envoyés inchangés}",
    EN: "Extractions applied · {n} sent {n|lead|leads} unchanged",
  },

  // ── Lead card ──
  verdictsTitle: { FR: "Revue IA", EN: "AI review" },
  verdictPassed: { FR: "Retenu", EN: "Passed" },
  verdictFailed: { FR: "Écarté", EN: "Failed" },
  verdictSource: { FR: "Source", EN: "Source" },

  // ── Stats ──
  statsLeads: { FR: "Leads trouvés", EN: "Leads found" },
  statsStepRate: {
    FR: "{pct} % de l'étape précédente",
    EN: "{pct}% of the previous step",
  },
  statsAlone: {
    FR: "Seule : {pct} % retenus",
    EN: "On its own: {pct}% kept",
  },

  // ── CRM ──
  crmUpcomingHint: {
    FR: "{n} {n|lead retenu partira|leads retenus partiront} au prochain envoi",
    EN: "{n} kept {n|lead goes|leads go} out with the next send",
  },
} satisfies FullTranslation

export type ExtractionsTrKey = keyof typeof EXTRACTIONS_T
